import { Directive, EventEmitter, Input, OnInit, Output, ViewContainerRef } from "@angular/core";
import { NotFoundComponent } from "../core/not-found/not-found.component";
import { LogCategory, LogDirective, LoggerService, LogType } from "../core/services/logger.service";
import { ProfileService } from "../core/services/profile.service";
import { ComponentData, ComponentMeta, ContentComponentData } from "../shared/component/component-meta";
import { IComponentData } from "../shared/component/i-component-component";
import { AgendaComponent } from "../widgets/agenda/agenda.component";
import { AnnouncementsComponent } from "../widgets/announcements/announcements.component";
import { CalendarComponent } from "../widgets/calendar/calendar.component";
import { CanvasAssignmentsComponent } from "../widgets/canvas-assignments/canvas-assignments.component";
import { EventsComponent } from "../widgets/careers/events/events.component";
import { JobPostingsComponent } from "../widgets/careers/job-postings/job-postings.component";
import { ContactsComponent } from "../widgets/contacts/contacts.component";
import { CmsComponent } from "../widgets/content/cms.component";
import { FacultyCoursesComponent } from "../widgets/faculty-courses/faculty-courses.component";
import { KudosComponent } from "../widgets/kudos/kudos.component";
import { MyCoursesComponent } from "../widgets/my-courses/my-courses.component";
import { MyDegreeComponent } from "../widgets/my-degree/my-degree.component";
import { MyDocumentsComponent } from "../widgets/my-documents/my-documents.component";
import { MyEquipmentComponent } from "../widgets/my-equipment/my-equipment.component";
import { MyFavoritesComponent } from "../widgets/my-favorites/my-favorites.component";
import { MyLinksComponent } from "../widgets/my-links/my-links.component";
import { MyPagesComponent } from "../widgets/my-pages/my-pages.component";
import { MySupportRequestsComponent } from "../widgets/my-support-requests/my-support-requests.component";
import { TilePageComponent } from "../widgets/tile-page/tile-page.component";
import { TransferCreditsComponent } from "../widgets/transfer-credits/transfer-credits.component";

@Directive({
    selector: '[appComponentsHost]',
    standalone: false
})
export class ComponentsHostDirective implements OnInit {

    // There's a good article on Dynamic Component Loading here: https://angular.io/guide/dynamic-component-loader 

    @Input() profileData: any;
    @Input() componentName: string;
    @Input() moduleName: string;
    @Input() pageID: string;
    @Output() onLoad = new EventEmitter<ComponentData>();
        
    private isChromeless: boolean = false;

    constructor(
        public viewContainerRef: ViewContainerRef,
        private profileService: ProfileService,
        //private componentFactoryResolver: ComponentFactoryResolver,
        private logger: LoggerService,
    ) {
        this.log("constructor", "", LogCategory.Constructor);
        
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Directive, LogDirective.ComponentsHost, category);
    }

    ngOnInit() {
        this.log("ngOnInit");        
        if (this.profileData) {            

            //Get the list of components available to use for opening in a modal
            const metaStore = this.profileService.componentStore;            
            this.viewContainerRef.clear();
            if (!!metaStore[this.componentName]) {   // If we have meta for it, then continue
                var item = this.createComponent(this.componentName, metaStore[this.componentName], this.profileService.widgetBetas.includes(this.componentName));
                //const componentFactory = this.componentFactoryResolver.resolveComponentFactory(item.component);
                const componentRef = this.viewContainerRef.createComponent(item.component);
                (<IComponentData>componentRef.instance).data = item;
                this.onLoad.emit(item);
            } else { // this user does not have the requested component (could be an incorrect audience)
                var blankMeta = new ComponentMeta("NotFound");
                var item = this.createComponent("NotFound", blankMeta, false);                
                const componentRef = this.viewContainerRef.createComponent(item.component);
                (<IComponentData>componentRef.instance).data = item;
                this.onLoad.emit(item);
            }
        }
    }



    private createComponent(name: string, meta: ComponentMeta, isBeta: boolean ): ComponentData {
        this.log("createComponent", `Module ${this.moduleName}; Widget ${name}`);
        if (isBeta)
            meta.IsBeta = true;

        if (this.pageID != '') {
            meta.PageID = this.pageID;
            meta.IsModal = true;
        }
                
        switch (name) {
            case "MyCourses":
                return new ComponentData(this.moduleName, MyCoursesComponent, meta);
            case "JobPostings":
                return new ComponentData(this.moduleName, JobPostingsComponent, meta);
            case "Events":
                return new ComponentData(this.moduleName, EventsComponent, meta);
            case "Announcements":
                return new ComponentData(this.moduleName, AnnouncementsComponent, meta);
            case "Contacts":
                return new ComponentData(this.moduleName, ContactsComponent, meta);
            case "Documents":
                return new ContentComponentData("document", this.moduleName, CmsComponent, meta);
            case "Links":
                return new ContentComponentData("link", this.moduleName, CmsComponent, meta);           
            case "Calendar":
                return new ComponentData(this.moduleName, CalendarComponent, meta);
            case "CanvasAssignments":
                return new ComponentData(this.moduleName, CanvasAssignmentsComponent, meta);
            case "Forms":
                return new ContentComponentData("form", this.moduleName, CmsComponent, meta);           
            case "FacultyCourses":
                return new ComponentData(this.moduleName, FacultyCoursesComponent, meta);
            case "FacultyInformation":
                return new ContentComponentData("instructional-suppor", this.moduleName, CmsComponent, meta);
            case "Kudos":
                return new ComponentData(this.moduleName, KudosComponent, meta);
            case "MySupportRequests":
                return new ComponentData(this.moduleName, MySupportRequestsComponent, meta);
            case "TransferCredits":
                return new ComponentData(this.moduleName, TransferCreditsComponent, meta);
            case "Agenda":
                return new ComponentData(this.moduleName, AgendaComponent, meta);
            case "MyDegree":
                return new ComponentData(this.moduleName, MyDegreeComponent, meta);
            case "Finance":
                return new ContentComponentData("finance", this.moduleName, CmsComponent, meta);
            case "Crises":
                return new ContentComponentData("crisis", this.moduleName, CmsComponent, meta);       
            case "MyLinks":
                return new ComponentData(this.moduleName, MyLinksComponent, meta);
            case "MyDocuments":
                return new ComponentData(this.moduleName, MyDocumentsComponent, meta);
            case "MyFavorites":
                return new ComponentData(this.moduleName, MyFavoritesComponent, meta);
            case "MyEquipment":
                return new ComponentData(this.moduleName, MyEquipmentComponent, meta);
            case "TilePage":
                return new ComponentData(this.moduleName, TilePageComponent, meta);
            case "MyPages":
                return new ComponentData(this.moduleName, MyPagesComponent, meta);
            default:
                return new ComponentData(this.moduleName, NotFoundComponent, meta);            
        }
    }

}
